import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, formatTime, formatDateTime } from '../utils/formatters';
import { computeBreakScore } from '../utils/breakScore';
import ScoreCell from './ScoreCell';
import BreakPriceTimeline from './BreakPriceTimeline';
import BreakTimeline from './BreakTimeline';
import ThreadPanel from './ThreadPanel';
import { downloadSpotsCsv } from '../utils/breakSpots';

// View-only twin of the break detail panel on the Breaks page. It must show
// everything that panel shows, minus the controls that change data — when the
// editable panel gains or changes something, change it here too.

// Mirrors helpers in Breaks.jsx — kept inline so this panel can be embedded
// anywhere without depending on the Breaks page module.
const getItemName  = (p) => p.product_id ? p.product_name : p.custom_name;
const getItemPrice = (p) => p.product_id ? (p.price || 0) : (p.custom_price || 0);
const getItemCogs  = (p) => p.product_id ? p.cogs : p.custom_cogs;
const getItemSku   = (p) => p.product_id ? p.sku : null;
const statusBadgeClass = s =>
  s === 'completed' ? 'badge-success'
  : s === 'cancelled' ? 'badge-danger'
  : s === 'live' ? 'badge-info'
  : 'badge-warning';

function ReadOnlyProductTable({ products }) {
  if (!products.length) return <div style={{ fontSize: '13px', color: 'var(--text-light)', padding: '8px 0' }}>No products added</div>;
  const totalPrice = products.reduce((s, p) => s + getItemPrice(p) * p.quantity, 0);
  const totalCogs  = products.reduce((s, p) => s + (getItemCogs(p) || 0) * p.quantity, 0);
  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th style={{ textAlign: 'center' }}>Qty</th>
            <th style={{ textAlign: 'right' }}>Price</th>
            <th style={{ textAlign: 'right' }}>COGS</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const isCustom = !p.product_id;
            return (
              <tr key={p.id}>
                <td>
                  <div style={{ fontWeight: 500, fontSize: '13px' }}>
                    {getItemName(p)}
                    {p.is_free ? <span className="badge badge-info" style={{ marginLeft: '6px' }}>Free</span> : null}
                    {(isCustom || p.category === 'Custom') && <span className="badge badge-warning" style={{ marginLeft: '6px' }}>Custom</span>}
                  </div>
                  {getItemSku(p) && <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{getItemSku(p)}</div>}
                </td>
                <td style={{ textAlign: 'center' }}>{p.quantity}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(getItemPrice(p) * p.quantity)}</td>
                <td style={{ textAlign: 'right' }}>{getItemCogs(p) != null ? formatCurrency(getItemCogs(p) * p.quantity) : '--'}</td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <td>Totals</td>
            <td style={{ textAlign: 'center' }}>{products.reduce((s, p) => s + p.quantity, 0)}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(totalPrice)}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(totalCogs)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function BreakDetailPanel({ breakId, canSeeFinancials = true, onClose }) {
  const [brk, setBrk] = useState(null);
  const [products, setProducts] = useState([]);
  const [spots, setSpots] = useState([]);
  const [highBidSpotId, setHighBidSpotId] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHitsSummary, setShowHitsSummary] = useState(false);
  const [showHittersSkunks, setShowHittersSkunks] = useState(false);
  const [showTrades, setShowTrades] = useState(false);
  const [showTradeIns, setShowTradeIns] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!breakId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/api/breaks/${breakId}`),
      api.get(`/api/breaks/${breakId}/spots`),
      api.get(`/api/breaks/${breakId}/trades`),
      // Trade-ins are a nice-to-have here; a failure must not blank the panel.
      api.get(`/api/breaks/${breakId}/trade-ins`).catch(() => ({ data: {} })),
    ]).then(([brkRes, spotsRes, tradesRes, tradeInsRes]) => {
      if (cancelled) return;
      setBrk(brkRes.data.break);
      setProducts(brkRes.data.break?.products || []);
      setSpots(spotsRes.data.spots || []);
      setHighBidSpotId(spotsRes.data.high_bid_spot_id ?? null);
      setTrades(tradesRes.data.trades || []);
      setTradeIns(tradeInsRes.data.trade_ins || []);
      setLoading(false);
    }).catch(err => {
      if (cancelled) return;
      setError(err.response?.data?.error || 'Failed to load break');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [breakId]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!breakId) return null;

  return (
    <div className="modal-overlay" onClick={() => onClose?.()}>
      <div className="detail-panel" onClick={e => e.stopPropagation()}>
        <div className="detail-panel-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            {loading ? 'Loading…' : `#${brk?.break_number ?? '--'} ${brk?.name || ''}`}
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-light)', padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4, marginLeft: 4 }}>
              View only
            </span>
          </h3>
          <button className="detail-panel-close" onClick={() => onClose?.()}>&times;</button>
        </div>

        {error && <div style={{ padding: '12px 0', color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

        {!loading && brk && (
          <>
            <div className="break-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label>Date</label>
                <div><span>{formatDate(brk.date)}</span></div>
              </div>
              <div>
                <label>Time</label>
                <div><span>{brk.scheduled_time ? formatTime(brk.scheduled_time) : 'Not set'}</span></div>
              </div>
              <div>
                <label>Host</label>
                <div><span>{brk.host_name || 'Unassigned'}</span></div>
              </div>
              <div>
                <label>Status</label>
                <div><span className={`badge ${statusBadgeClass(brk.status)}`}>{brk.status}</span></div>
              </div>
              <div>
                <label>Category</label>
                <div><span>{brk.category || 'None'}</span></div>
              </div>
              <div>
                <label>Spot</label>
                <div><span>{brk.spot_type || 'Team'}</span></div>
              </div>
              {/* The Airtable case(s) this break rips — linked on the Break Spots page.
                  Same cell in both break panels (Breaks.jsx and BreakDetailPanel.jsx). */}
              <div>
                <label>Case</label>
                <div>
                  <span>
                    {(brk.cases || []).length > 0
                      ? brk.cases.map(c => `${c.case_number} · ${c.card_count} card${c.card_count === 1 ? '' : 's'}`).join(', ')
                      : 'None'}
                  </span>
                </div>
              </div>

              {(() => {
                // Mirror the editable Break Spots page exactly:
                //   - totalSpots = category-derived total (32 NFL, 30 NBA, 8 Division, etc.)
                //   - soldPrices = only spots with a real price (drop null/0)
                //   - cogs = break_products sum (price × qty)
                // The earlier version of this panel used spots.length and
                // breaks.actual_cogs/expected_cogs, which gave different
                // (and wrong) numbers when not every spot was upserted yet.
                const filledPrices = spots.map(s => Number(s.price)).filter(p => p && p > 0);
                const actualPrice = filledPrices.reduce((a, b) => a + b, 0);
                const totalSpots = brk.total_spots || spots.length || 0;
                const breakCogs = (products || []).reduce(
                  (s, p) => s + (Number(getItemCogs(p)) || 0) * Number(p.quantity || 0),
                  0
                );
                const avgSold = filledPrices.length > 0 ? actualPrice / filledPrices.length : 0;
                const projectedRevenue = avgSold * totalSpots;
                const detailMargin = projectedRevenue > 0
                  ? ((projectedRevenue - breakCogs) / projectedRevenue) * 100
                  : null;
                const isFinalized = brk.status === 'completed' || brk.status === 'pending';
                const lockedScore = isFinalized && brk.final_score != null ? brk.final_score : null;
                const liveScore = computeBreakScore({
                  totalSpots,
                  soldPrices: filledPrices,
                  cogs: breakCogs,
                });
                const scoreVal = lockedScore != null ? lockedScore : (liveScore?.score ?? null);
                const scoreLabel = isFinalized ? 'Final Score' : 'Projected Score';
                return (
                  <>
                    {canSeeFinancials ? (
                      <>
                        <div>
                          <label>Actual Price</label>
                          <div>{actualPrice ? formatCurrency(actualPrice) : '--'}</div>
                        </div>
                        <div>
                          <label>{brk.status === 'live' ? 'Projected Margin' : 'Margin'}</label>
                          <div style={{ color: detailMargin != null ? (detailMargin >= 0 ? 'var(--success, green)' : 'var(--danger, red)') : undefined }}>
                            {detailMargin != null ? `${detailMargin.toFixed(1)}%` : '--'}
                          </div>
                        </div>
                      </>
                    ) : (<><div /><div /></>)}
                    <div>
                      <label>Spots Sold</label>
                      <div>{filledPrices.length}/{totalSpots || '--'}</div>
                    </div>
                    {/* Second stats row: Spots Sold, Actual PPS, then the score
                        (final or projected) to its right. Break Spots sits in
                        column 3 of the row below — where the editable panel
                        has it beside its Confirm / Surprise Set buttons. */}
                    {canSeeFinancials && (
                      <div>
                        <label>Actual PPS</label>
                        <div>{avgSold > 0 ? formatCurrency(avgSold) : '--'}</div>
                      </div>
                    )}
                    <div>
                      <label>{brk.status === 'live' ? 'Projected Score' : (scoreLabel || 'Break Score')}</label>
                      {scoreVal == null ? (
                        <div style={{ fontSize: '12px', color: 'var(--text-light)', lineHeight: 1.3 }}>
                          Score unavailable — enter COGS and at least one spot price to begin scoring
                        </div>
                      ) : (
                        <ScoreCell
                          score={scoreVal}
                          label={scoreLabel}
                          breakdown={liveScore}
                          showBreakdown={canSeeFinancials}
                        />
                      )}
                    </div>
                  </>
                );
              })()}
              <div className="stat-pin-col3">
                <label>&nbsp;</label>
                <div>
                  <button className="btn btn-primary btn-sm" onClick={() => { onClose?.(); navigate(`/breaks/${brk.break_number}/spots`); }}>Break Spots</button>
                </div>
              </div>

              {brk.hits_confirmed_at && (
                <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-light)' }}>
                  Hits confirmed by {brk.hits_confirmed_by_name || 'user'} on {formatDateTime(brk.hits_confirmed_at)}
                </div>
              )}
              {brk.stickies_confirmed_at && (
                <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-light)' }}>
                  Stickies confirmed by {brk.stickies_confirmed_by_name || 'user'} on {formatDateTime(brk.stickies_confirmed_at)}
                </div>
              )}
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <BreakTimeline brk={brk} canEdit={false} />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '4px' }}>Price Timeline</h4>
              <BreakPriceTimeline breakId={breakId} live compact />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Break Products</h4>
              <ReadOnlyProductTable products={products.filter(p => !p.is_free)} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Product Additions</h4>
              <ReadOnlyProductTable products={products.filter(p => p.is_free)} />
            </div>

            {spots.length > 0 && (() => {
              const showFreebies = spots.some(s => s.freebie?.trim());
              const hits = spots.filter(s => s.customer?.trim() && (s.hit?.trim() || s.freebie?.trim()));
              if (hits.length === 0) return null;
              return (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowHitsSummary(v => !v)}>
                    <span style={{ display: 'inline-block', transform: showHitsSummary ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                    Hits Summary
                  </h4>
                  {showHitsSummary && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Customer</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Team</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Hit</th>
                          {showFreebies && <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Freebie(s)</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {hits.map((s, i) => (
                          <tr key={i}>
                            <td title={s.id === highBidSpotId ? 'High bid' : undefined} style={{
                              padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px',
                              ...(s.id === highBidSpotId ? { outline: '2px solid var(--warning)', outlineOffset: '-2px', borderRadius: 4 } : {}),
                            }}>{s.customer}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{(s.team || '').replace(/\s*\|\s*/g, ' | ')}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{s.hit}</td>
                            {showFreebies && <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{s.freebie || ''}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {spots.length > 0 && (() => {
              const customerSpots = {};
              spots.forEach(s => {
                if (!s.customer?.trim()) return;
                if (!customerSpots[s.customer]) customerSpots[s.customer] = [];
                customerSpots[s.customer].push(s);
              });
              const hitters = Object.keys(customerSpots).filter(c => customerSpots[c].some(s => s.hit?.trim()));
              const skunks  = Object.keys(customerSpots).filter(c => !customerSpots[c].some(s => s.hit?.trim()));
              if (hitters.length === 0 && skunks.length === 0) return null;
              return (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowHittersSkunks(v => !v)}>
                    <span style={{ display: 'inline-block', transform: showHittersSkunks ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                    Hitters / Skunks
                  </h4>
                  {showHittersSkunks && (
                    <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--success)', marginBottom: '4px' }}>Hitters</div>
                        {hitters.length > 0 ? hitters.map(c => (
                          <div key={c} style={{ fontSize: '13px', padding: '2px 0' }}>{c} ({customerSpots[c].length})</div>
                        )) : <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>None</div>}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--danger)', marginBottom: '4px' }}>Skunks</div>
                        {skunks.length > 0 ? skunks.map(c => (
                          <div key={c} style={{ fontSize: '13px', padding: '2px 0' }}>{c} ({customerSpots[c].length})</div>
                        )) : <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>None</div>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {trades.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowTrades(v => !v)}>
                  <span style={{ display: 'inline-block', transform: showTrades ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                  Team Trades
                </h4>
                {showTrades && (
                  <div style={{ marginTop: '8px' }}>
                    {(() => {
                      const displayTeam = team => team && team.includes('|') ? team.split('|')[0] : team;
                      const groups = [];
                      for (const t of trades) {
                        if (t.trade_group_id == null) {
                          groups.push({ kind: 'single', t });
                        } else {
                          const last = groups[groups.length - 1];
                          if (last && last.kind === 'multi' && last.groupId === t.trade_group_id) {
                            last.legs.push(t);
                          } else {
                            groups.push({ kind: 'multi', groupId: t.trade_group_id, legs: [t] });
                          }
                        }
                      }
                      return groups.map((g, idx) => {
                        if (g.kind === 'single') {
                          const t = g.t;
                          return (
                            <div key={t.id} style={{ fontSize: '13px', padding: '2px 0' }}>
                              {t.customer_a} | {displayTeam(t.team_a)} ↔ {t.customer_b} | {displayTeam(t.team_b)}
                            </div>
                          );
                        }
                        const byGiver = new Map();
                        for (const leg of g.legs) {
                          if (!byGiver.has(leg.customer_a)) byGiver.set(leg.customer_a, []);
                          byGiver.get(leg.customer_a).push(displayTeam(leg.team_a));
                        }
                        const parts = [...byGiver.entries()].map(([giver, teams]) => `${giver} gave ${teams.join(', ')}`);
                        return (
                          <div key={`g-${g.groupId}-${idx}`} style={{ fontSize: '13px', padding: '2px 0' }}>
                            {parts.join('  ↔  ')}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            {tradeIns.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowTradeIns(v => !v)}>
                  <span style={{ display: 'inline-block', transform: showTradeIns ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                  Team Trade-Ins
                </h4>
                {showTradeIns && (
                  <div style={{ marginTop: '8px' }}>
                    {tradeIns.map(ti => (
                      <div key={ti.id} style={{ fontSize: '13px', padding: '2px 0' }}>
                        {ti.customer} | {ti.source_a_team} (${Math.round(Number(ti.source_a_price) || 0)}) + {ti.source_b_team} (${Math.round(Number(ti.source_b_price) || 0)}) → {ti.target_team} (${Math.round(Number(ti.target_price) || 0)})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <ThreadPanel entityType="break" entityId={brk.id} />

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px', marginBottom: '16px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadSpotsCsv(brk, spots)}>Download as CSV</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
